import { useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { useStartJob } from '@diggens/shared-hooks';
import type { AgentRosterEntry } from '@diggens/types';

interface JobDialogProps {
  agent: AgentRosterEntry;
  onClose: () => void;
  /** Called with the job's run once it has started. */
  onStarted: (runId: string) => void;
}

/** Gives one recruited specialist a job. Starting it is the approval, so it runs at once. */
export function JobDialog({ agent, onClose, onStarted }: JobDialogProps) {
  const [task, setTask] = useState('');
  const startJob = useStartJob();

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Give {agent.displayName} a job</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          {agent.description}
        </Typography>
        <TextField
          autoFocus
          multiline
          minRows={4}
          fullWidth
          label="What should it do?"
          placeholder="Everything it needs: who or what, and what you want back"
          value={task}
          onChange={(event) => setTask(event.target.value)}
          slotProps={{ htmlInput: { maxLength: 4000 } }}
          sx={{ mt: 2 }}
        />
        {agent.writes && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {agent.displayName} changes data. Starting this job approves that, so it runs without asking again.
          </Alert>
        )}
        {startJob.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {startJob.error.message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!task.trim() || startJob.isPending}
          onClick={() => startJob.mutate({ agent: agent.agent, task: task.trim() }, { onSuccess: ({ runId }) => onStarted(runId) })}
        >
          Start job
        </Button>
      </DialogActions>
    </Dialog>
  );
}
